import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useT } from '../../renderer/TextsProvider.js';
import { cssColor } from '../../sections/_shared/settingValue.js';
import { marginFields, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, num, str, visibilityCss } from '../_shared/blockRender.js';
import { useWelcomeForm } from '../_shared/welcomeForm.js';
/**
 * `welcome-field` — UN champ de saisie de la fenêtre de bienvenue (feature 0074, lot B).
 *
 * Un bloc par champ, comme chez Klaviyo : la vendeuse ajoute l'adresse e-mail, et ajoute le téléphone
 * seulement si elle en veut un. Ce que le champ DEMANDE est un réglage (`kind`), pas un type de bloc —
 * trois blocs quasi identiques dans le menu « Ajouter » coûteraient trois fois le même apprentissage.
 *
 * **Le `name` de l'entrée n'est pas décoratif.** L'îlot de la vitrine lit le formulaire avec `FormData`
 * au moment du clic ; c'est ce nom qui lui dit laquelle des valeurs est l'adresse à inscrire. Il est donc
 * dérivé de `kind` et jamais configurable.
 *
 * **Le texte d'invite part VIDE.** Un défaut écrit ici serait figé dans le document de la boutique, dans
 * UNE langue, à l'instant où la fenêtre est créée — le piège que la page de remerciement a documenté.
 * Vide veut dire « garde la formulation de la boutique, dans la langue de la boutique » ; seule une
 * vendeuse qui tape quelque chose la reprend à son compte.
 */
export const welcomeFieldBlockSchema = {
    type: 'welcome-field',
    name: 'Form field',
    fields: [
        visibilityField,
        {
            id: 'kind',
            type: 'select',
            label: 'What it asks for',
            info: 'The e-mail is the one that subscribes the visitor to your list. The others are collected with it.',
            default: 'email',
            options: [
                { value: 'email', label: 'E-mail address' },
                { value: 'phone', label: 'Phone number' },
                { value: 'name', label: 'Name' },
            ],
        },
        {
            id: 'placeholder',
            type: 'text',
            label: 'Placeholder',
            info: 'Leave empty to keep your shop’s own wording, in your shop’s language.',
            default: '',
        },
        {
            id: 'required',
            type: 'checkbox',
            label: 'Required',
            info: 'An empty required field stops the send and the browser says so.',
            default: true,
        },
        {
            id: 'backgroundColor',
            type: 'color',
            label: 'Background',
            group: 'Colors',
            default: { ref: 'custom', value: '#FFFFFF' },
        },
        {
            id: 'textColor',
            type: 'color',
            label: 'Text',
            group: 'Colors',
            default: { ref: 'custom', value: '#151515' },
        },
        {
            id: 'borderColor',
            type: 'color',
            label: 'Border',
            group: 'Colors',
            default: { ref: 'custom', value: '#D8D4CE' },
        },
        {
            id: 'borderWidth',
            type: 'range',
            label: 'Border width',
            group: 'Colors',
            min: 0,
            max: 4,
            step: 1,
            default: 1,
            unit: 'px',
        },
        {
            id: 'cornerRadius',
            type: 'range',
            label: 'Corner radius',
            group: 'Layout',
            min: 0,
            max: 32,
            step: 1,
            default: 10,
            unit: 'px',
        },
        {
            id: 'fontSize',
            type: 'range',
            label: 'Font size',
            group: 'Layout',
            min: 12,
            max: 20,
            step: 1,
            default: 15,
            unit: 'px',
        },
        {
            id: 'paddingY',
            type: 'range',
            label: 'Height',
            group: 'Layout',
            info: 'The space above and below the text inside the field.',
            min: 6,
            max: 22,
            step: 1,
            default: 12,
            unit: 'px',
        },
        ...marginFields(0, 9),
    ],
};
/** `kind` → le nom de l'entrée (ce que `FormData` rend à l'îlot), son type HTML et son autocomplétion. */
const KINDS = {
    email: {
        name: 'email',
        type: 'email',
        autoComplete: 'email',
        hint: 'footer.email_placeholder',
    },
    phone: { name: 'phone', type: 'tel', autoComplete: 'tel', hint: 'cod.default_field_phone' },
    name: { name: 'name', type: 'text', autoComplete: 'name', hint: 'cod.default_field_name' },
};
/**
 * Un champ, et rien d'autre. Pas d'état React : la valeur vit dans le DOM et l'îlot la lit au clic —
 * c'est ce qui laisse la vendeuse ajouter et retirer des champs sans qu'aucun code ne change.
 */
const WelcomeFieldBlock = ({ block }) => {
    const bs = block.settings;
    const cls = `aqx-wpf-${block.id}`;
    const t = useT();
    const { busy } = useWelcomeForm();
    const kind = KINDS[str(bs.kind, 'email')] ?? KINDS.email;
    const placeholder = str(bs.placeholder).trim() || t(kind.hint);
    const radius = num(bs.cornerRadius, 10);
    const border = num(bs.borderWidth, 1);
    const style = {
        inlineSize: '100%',
        display: 'block',
        boxSizing: 'border-box',
        background: cssColor(bs.backgroundColor, '#FFFFFF'),
        color: cssColor(bs.textColor, '#151515'),
        border: border > 0 ? `${border}px solid ${cssColor(bs.borderColor, '#D8D4CE')}` : 'none',
        borderRadius: `${radius}px`,
        fontSize: `${num(bs.fontSize, 15)}px`,
        padding: `${num(bs.paddingY, 12)}px 14px`,
        font: 'inherit',
        fontFamily: 'inherit',
        ...marginStyle(bs, 0, 9),
    };
    const css = visibilityCss(cls, bs.visibility);
    return (_jsxs(_Fragment, { children: [css ? _jsx("style", { dangerouslySetInnerHTML: { __html: css } }) : null, _jsx("input", { className: cls, style: style, type: kind.type, name: kind.name, autoComplete: kind.autoComplete, inputMode: kind.type === 'email' ? 'email' : kind.type === 'tel' ? 'tel' : undefined, enterKeyHint: "send", maxLength: 254, required: bs.required !== false, disabled: busy, placeholder: placeholder, "aria-label": placeholder })] }));
};
export const welcomeFieldBlock = {
    type: 'welcome-field',
    schema: welcomeFieldBlockSchema,
    Component: WelcomeFieldBlock,
    meta: { label: 'Form field', icon: 'mail' },
};
//# sourceMappingURL=index.js.map